import { useState, useEffect, useCallback } from "react";
import { Search, Trash2 } from "lucide-react";
import styles from "../../../styles/__labServicesAndPricing.module.scss";
import dialogStyles from "../../../styles/__labDialogAddEditTest.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import { IoAddCircleOutline } from "react-icons/io5";
import LabDialogAddEditTest from "../../../components/lab-dialog-AddEditTest/LabDialogAddEditTest";
import {
  fetchImagingServices,
  deleteImagingService,
} from "../../../reducer/imagingServices/imagingServicesReducer";

// Status is currently hidden from the Services & Pricing UI (services default to
// ACTIVE). Flip this to true to bring the Status column back.
const SHOW_STATUS_COLUMN = false;

const ServicesAndPricing = ({ onLogout, onNavigateToDashboard }) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const dispatch = useDispatch();

  const [services, setServices] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("All");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("add"); // "add" | "edit"
  const [selectedService, setSelectedService] = useState(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteServiceId, setDeleteServiceId] = useState(null);
  const [deletingService, setDeletingService] = useState(false);

  const loadServices = useCallback(
    async (page = 1, search = "", statusFilter = activeTab) => {
      setLoading(true);
      try {
        const params = { page, limit: 10 };
        if (search) params.search = search;
        if (statusFilter && statusFilter !== "All")
          params.status = statusFilter === "Active" ? "ACTIVE" : "INACTIVE";
        const result = await dispatch(fetchImagingServices(params)).unwrap();
        setServices(result.services || []);
        setPagination(
          result.pagination || { page, limit: 10, total: 0, totalPages: 1 },
        );
        setCurrentPage(page);
      } catch {
        setServices([]);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, activeTab],
  );

  useEffect(() => {
    loadServices(1);
  }, [loadServices]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    loadServices(1, val, activeTab);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    loadServices(page, searchTerm);
  };

  const handleAddClick = () => {
    setModalType("add");
    setSelectedService(null);
    setModalOpen(true);
  };

  const handleEditClick = (service) => {
    setModalType("edit");
    setSelectedService(service);
    setModalOpen(true);
  };

  // Open the confirmation modal instead of the native confirm dialog.
  const handleDeleteClick = (serviceId) => {
    setDeleteServiceId(serviceId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingService) return;
    setShowDeleteModal(false);
    setDeleteServiceId(null);
  };

  const confirmDeleteService = async () => {
    if (!deleteServiceId) return;
    setDeletingService(true);
    try {
      await dispatch(deleteImagingService(deleteServiceId)).unwrap();
      setShowDeleteModal(false);
      setDeleteServiceId(null);
      loadServices(currentPage, searchTerm);
    } catch {
      // toast shown in reducer
    } finally {
      setDeletingService(false);
    }
  };

  const handleModalSuccess = () => {
    loadServices(currentPage, searchTerm);
  };

  const EditIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.70809 19.8987C5.25793 19.8987 4.87743 19.7432 4.56659 19.4324C4.25576 19.1216 4.10034 18.7411 4.10034 18.2909V5.70641C4.10034 5.25624 4.25576 4.87574 4.56659 4.56491C4.87743 4.25407 5.25793 4.09866 5.70809 4.09866H12.3351C12.5518 4.09866 12.7143 4.16674 12.8226 4.30291C12.9309 4.43924 12.9851 4.58782 12.9851 4.74866C12.9851 4.90949 12.9307 5.05807 12.8218 5.19441C12.713 5.33057 12.5539 5.39866 12.3446 5.39866H5.70809C5.63109 5.39866 5.56059 5.43074 5.49659 5.49491C5.43243 5.55891 5.40034 5.62941 5.40034 5.70641V18.2909C5.40034 18.3679 5.43243 18.4384 5.49659 18.5024C5.56059 18.5666 5.63109 18.5987 5.70809 18.5987H18.2926C18.3696 18.5987 18.4401 18.5666 18.5041 18.5024C18.5683 18.4384 18.6003 18.3679 18.6003 18.2909V12.2037C18.6003 11.9952 18.6684 11.8367 18.8046 11.7284C18.9409 11.6201 19.0895 11.5659 19.2503 11.5659C19.4112 11.5659 19.5598 11.6201 19.6961 11.7284C19.8323 11.8367 19.9003 11.9992 19.9003 12.2159V18.2909C19.9003 18.7411 19.7449 19.1216 19.4341 19.4324C19.1233 19.7432 18.7428 19.8987 18.2926 19.8987H5.70809ZM10.1003 13.0994V11.5722C10.1003 11.359 10.1388 11.1537 10.2158 10.9564C10.2927 10.7589 10.4103 10.581 10.5688 10.4227L18.8351 2.15641C18.9734 2.01791 19.1189 1.92141 19.2716 1.86691C19.4241 1.81241 19.584 1.78516 19.7513 1.78516C19.9208 1.78516 20.0838 1.81241 20.2403 1.86691C20.397 1.92141 20.5408 2.01291 20.6718 2.14141L21.8041 3.24866C21.9489 3.38716 22.0518 3.53899 22.1128 3.70416C22.1737 3.86932 22.2041 4.03674 22.2041 4.20641C22.2041 4.37641 22.1751 4.54099 22.1171 4.70016C22.0591 4.85932 21.9612 5.00749 21.8233 5.14466L13.5283 13.4399C13.3698 13.5982 13.1923 13.7143 12.9958 13.7882C12.7992 13.8618 12.5946 13.8987 12.3821 13.8987H10.9088C10.6813 13.8987 10.4898 13.8225 10.3341 13.6702C10.1783 13.5177 10.1003 13.3274 10.1003 13.0994ZM11.4003 12.5987H12.5216L18.7291 6.39091L18.1811 5.81791L17.5888 5.25066L11.4003 11.4392V12.5987Z"
        fill="#3B3B48"
      />
    </svg>
  );

  const DeleteIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.30775 20.4997C6.81058 20.4997 6.385 20.3227 6.031 19.9687C5.677 19.6147 5.5 19.1892 5.5 18.692V5.99973H5.25C5.0375 5.99973 4.85942 5.92782 4.71575 5.78398C4.57192 5.64015 4.5 5.46198 4.5 5.24948C4.5 5.03682 4.57192 4.85873 4.71575 4.71523C4.85942 4.57157 5.0375 4.49973 5.25 4.49973H9C9 4.2549 9.08625 4.04624 9.25875 3.87374C9.43108 3.7014 9.63967 3.61523 9.8845 3.61523H14.1155C14.3603 3.61523 14.5689 3.7014 14.7413 3.87374C14.9138 4.04624 15 4.2549 15 4.49973H18.75C18.9625 4.49973 19.1406 4.57165 19.2843 4.71548C19.4281 4.85932 19.5 5.03748 19.5 5.24998C19.5 5.46265 19.4281 5.64073 19.2843 5.78423C19.1406 5.9279 18.9625 5.99973 18.75 5.99973H18.5V18.692C18.5 19.1892 18.323 19.6147 17.969 19.9687C17.615 20.3227 17.1894 20.4997 16.6923 20.4997H7.30775ZM17 5.99973H7V18.692C7 18.7818 7.02883 18.8556 7.0865 18.9132C7.14417 18.9709 7.21792 18.9997 7.30775 18.9997H16.6923C16.7821 18.9997 16.8558 18.9709 16.9135 18.9132C16.9712 18.8556 17 18.7818 17 18.692V5.99973ZM10.1543 16.9997C10.3668 16.9997 10.5448 16.9279 10.6885 16.7842C10.832 16.6404 10.9037 16.4622 10.9037 16.2497V8.74973C10.9037 8.53723 10.8318 8.35907 10.688 8.21523C10.5443 8.07157 10.3662 7.99973 10.1535 7.99973C9.941 7.99973 9.76292 8.07157 9.61925 8.21523C9.47575 8.35907 9.404 8.53723 9.404 8.74973V16.2497C9.404 16.4622 9.47583 16.6404 9.6195 16.7842C9.76333 16.9279 9.94158 16.9997 10.1543 16.9997ZM13.8465 16.9997C14.059 16.9997 14.2371 16.9279 14.3807 16.7842C14.5243 16.6404 14.596 16.4622 14.596 16.2497V8.74973C14.596 8.53723 14.5242 8.35907 14.3805 8.21523C14.2367 8.07157 14.0584 7.99973 13.8458 7.99973C13.6333 7.99973 13.4552 8.07157 13.3115 8.21523C13.168 8.35907 13.0962 8.53723 13.0962 8.74973V16.2497C13.0962 16.4622 13.1682 16.6404 13.312 16.7842C13.4557 16.9279 13.6338 16.9997 13.8465 16.9997Z"
        fill="#EC003F"
      />
    </svg>
  );

  return (
    <LabDashboardLayout
      currentPage="services-pricing"
      onNavigate={(page) => page === "dashboard" && onNavigateToDashboard()}
      onLogout={onLogout}
    >
      {/* Add / Edit Modal */}
      <LabDialogAddEditTest
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        type={modalType}
        centre="imaging"
        serviceData={selectedService}
        onSuccess={handleModalSuccess}
      />

      <div className={styles["apt-page-header"]}>
        <div className={styles["apt-text-subtxt"]}>
          <h1>{isArabic ? "الخدمات والأسعار" : "Services and Pricing"}</h1>
          <p>
            {isArabic
              ? "إدارة الفحوصات المتاحة والأسعار."
              : "Manage available tests and prices."}
          </p>
        </div>
        <button className={styles["apt-register-btn"]} onClick={handleAddClick}>
          <IoAddCircleOutline /> {isArabic ? "إضافة فحص جديد" : "Add New Test"}
        </button>
      </div>

      <div className={styles["apt-appointments-section"]}>
        {/* Search */}
        <div className={styles["apt-filters"]}>
          <div className={styles["apt-filter-group"]}>
            <div className={styles["apt-search-wrapper"]}>
              <Search className={styles["apt-search-icon"]} />
              <input
                type="text"
                placeholder="Test Name"
                value={searchTerm}
                onChange={handleSearch}
                className={styles["apt-search-input"]}
              />
            </div>
            <button
              className={styles["apt-reset-btn"]}
              onClick={() => {
                setSearchTerm("");
                loadServices(1, "", activeTab);
              }}
            >
              {t("appointments.filters.button.reset")}
            </button>
          </div>
        </div>

        {/* Active/Inactive tabs hidden per request — only "All" is shown. */}
        <div className={styles["apt-appointment-tabs"]}>
          {["All"].map((tab) => (
            <button
              key={tab}
              className={`${styles["apt-tab"]} ${activeTab === tab ? styles["active"] : ""}`}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
                loadServices(1, searchTerm, tab);
              }}
            >
              {isArabic
                ? tab === "All"
                  ? "الكل"
                  : tab === "Active"
                    ? "نشط"
                    : "غير نشط"
                : tab}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className={styles["apt-appointments-table"]}>
          <table>
            <thead>
              <tr>
                <th>{isArabic ? "رمز الخدمة" : "Service Code"}</th>
                <th>{isArabic ? "اسم الفحص" : "Test Name"}</th>
                <th>{isArabic ? "الفئة" : "Category"}</th>
                <th>{isArabic ? "السعر (د.ع)" : "Price (IQD)"}</th>
                {SHOW_STATUS_COLUMN && (
                  <th>{isArabic ? "الحالة" : "Status"}</th>
                )}
                <th>{isArabic ? "الإجراء" : "Action"}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={SHOW_STATUS_COLUMN ? 6 : 5}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    {isArabic ? "جاري التحميل..." : "Loading..."}
                  </td>
                </tr>
              ) : services.length === 0 ? (
                <tr>
                  <td
                    colSpan={SHOW_STATUS_COLUMN ? 6 : 5}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    {isArabic ? "لا توجد خدمات" : "No services found"}
                  </td>
                </tr>
              ) : (
                services.map((item) => (
                  <tr key={item.id}>
                    <td>{item.serviceCode}</td>
                    <td>{item.serviceName}</td>
                    <td>
                      {isArabic
                        ? item.categoryNameArabic || item.categoryName || "—"
                        : item.categoryName || "—"}
                    </td>
                    <td>IQD {item.price?.toLocaleString()}</td>
                    {SHOW_STATUS_COLUMN && (
                      <td>
                        <span
                          style={{
                            padding: "2px 10px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: 500,
                            backgroundColor:
                              item.status === "ACTIVE" ? "#ECFDF5" : "#FEF2F2",
                            color:
                              item.status === "ACTIVE" ? "#059669" : "#DC2626",
                          }}
                        >
                          {item.status === "ACTIVE"
                            ? isArabic
                              ? "نشط"
                              : "Active"
                            : isArabic
                              ? "غير نشط"
                              : "Inactive"}
                        </span>
                      </td>
                    )}
                    <td>
                      <div className={styles["apt-action-buttons"]}>
                        <button
                          className={styles["apt-action-btn"]}
                          onClick={() => handleEditClick(item)}
                          title="Edit"
                        >
                          <EditIcon />
                        </button>
                        <button
                          className={styles["apt-action-btn"]}
                          onClick={() => handleDeleteClick(item.id)}
                          title="Deactivate"
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={styles["apt-pagination"]}>
          <div className={styles["apt-pagination-info"]}>
            Displaying Results{" "}
            {pagination.total === 0
              ? 0
              : (currentPage - 1) * pagination.limit + 1}{" "}
            to {Math.min(currentPage * pagination.limit, pagination.total)} Out
            of {pagination.total}
          </div>
          <div className={styles["apt-pagination-controls"]}>
            <button
              className={`${styles["apt-pagination-btn"]} ${currentPage === 1 ? styles["disabled"] : ""}`}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              {t("dashboard.pagination.previous")}
            </button>
            <div className={styles["apt-page-numbers"]}>
              {Array.from(
                { length: pagination.totalPages },
                (_, i) => i + 1,
              ).map((page) => (
                <button
                  key={page}
                  className={`${styles["apt-page-btn"]} ${currentPage === page ? styles["active"] : ""}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              className={`${styles["apt-pagination-btn"]} ${styles["apt-next-btn"]} ${currentPage === pagination.totalPages ? styles["disabled"] : ""}`}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.totalPages}
            >
              {t("dashboard.pagination.next")}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Test Confirmation Modal — styled like the Accept confirmation modal */}
      {showDeleteModal && (
        <div className={dialogStyles["labDialog_wrapper"]}>
          <div
            className={dialogStyles["dialog-overlay"]}
            style={{ alignItems: "center" }}
          >
            <div
              className={dialogStyles["dialog-box"]}
              style={{ width: "460px" }}
            >
              <button
                className={dialogStyles["dialog-close"]}
                onClick={closeDeleteModal}
                disabled={deletingService}
              >
                ×
              </button>
              <h2 className={dialogStyles["dialog-title"]}>
                {isArabic ? "حذف الفحص" : "Delete Test"}
              </h2>
              <p
                style={{
                  margin: "0 0 8px",
                  fontSize: "14px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                {isArabic
                  ? "هل أنت متأكد أنك تريد حذف هذا الفحص؟"
                  : "Are you sure you want to delete this test?"}
              </p>
              <div
                className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}
              >
                <button
                  className={dialogStyles["btn-outlined-1"]}
                  onClick={closeDeleteModal}
                  disabled={deletingService}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                  }}
                >
                  {isArabic ? "إلغاء" : "Cancel"}
                </button>
                <button
                  className={dialogStyles["btn-1"]}
                  onClick={confirmDeleteService}
                  disabled={deletingService}
                  style={{
                    opacity: deletingService ? 0.6 : 1,
                    justifyContent: "center",
                    border: "1px solid transparent",
                    boxSizing: "border-box",
                  }}
                >
                  <Trash2 size={16} />{" "}
                  {deletingService
                    ? isArabic
                      ? "جارٍ الحذف..."
                      : "Deleting..."
                    : isArabic
                      ? "حذف"
                      : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LabDashboardLayout>
  );
};

export default ServicesAndPricing;
